import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { ArrowRight, Zap, Target, TrendingUp, Users, Check, Star, Shield, Award } from 'lucide-react';
import Button from '../components/ui/Button';
import { whopService } from '../services/whopService';
import LumoPromo from '../components/LumoPromo';

const LandingPage = () => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const lifetimeLeft = 10;

  const faqLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'What is Earn My Edge?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Earn My Edge is an AI-powered sales training platform offering daily drills, objection handling practice, framework-guided lessons, and real-time coaching to help sales professionals close more deals.'
        }
      },
      {
        '@type': 'Question',
        name: 'How does the AI coaching work?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Submit your response to a drill or scenario, and our AI coach evaluates it across clarity, confidence, control, and close. You receive targeted feedback, strengths, improvements, and a sample response to guide your practice.'
        }
      },
      {
        '@type': 'Question',
        name: 'Which sales frameworks are included?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'SPIN Selling, Sandler, Challenger, MEDDIC, NEPQ, GAP Selling, Straight Line, and Value Selling — with guided lessons and practice scenarios for each.'
        }
      },
      {
        '@type': 'Question',
        name: 'Can I track my progress over time?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Yes. Scores are saved to your profile, XP is awarded for practice, and mastery is tracked over time. You can view your analytics and improvement trends in your dashboard.'
        }
      },
      {
        '@type': 'Question',
        name: 'Is there team access and management?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Yes. Earn My Edge supports team accounts with role-based access (owner, manager, member) and daily team analytics to track performance and progress across your sales organization.'
        }
      },
      {
        '@type': 'Question',
        name: 'What pricing options are available?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'One Elite subscription at $97/month or $999/year (save 2 months). We also offer a limited Lifetime Elite plan at $2,500 one-time for early adopters.'
        }
      }
    ]
  };

  const features = [
    {
      icon: <Target className="w-6 h-6" />,
      title: "Daily Sales Drills",
      description: "Practice real scenarios with AI-powered feedback. Master objection handling, closing techniques, and pitch refinement."
    },
    {
      icon: <Zap className="w-6 h-6" />,
      title: "Lumo AI Coach",
      description: "Get instant, personalized coaching using proprietary techniques like Mirror & Pivot, Shrug Close, and Frame Flip."
    },
    {
      icon: <TrendingUp className="w-6 h-6" />,
      title: "Track Real Results",
      description: "Log your deals and see which techniques drive wins. Adaptive learning optimizes your training based on performance."
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: "Team Management",
      description: "Collaborate with your sales team. Share insights, compete on leaderboards, and track collective progress."
    }
  ];

  const whopMainUrl = 'https://whop.com/earn-my-edge/earnmyedge/';

  const pricingPlans = [
    {
      name: "Elite Monthly",
      price: "$97",
      period: "/month",
      description: "Full access, billed monthly",
      features: [
        "All modules and drills unlocked",
        "AI Conversation Analyzer + roleplay",
        "Scripts, playbooks, and micro-lessons",
        "Priority processing and support"
      ],
      cta: "Get Started",
      popular: false,
      whopCheckout: 'plan_eXbZqIxnR13uz'
    },
    {
      name: "Elite Annual",
      price: "$999",
      period: "/year",
      description: "Best value - save ~2 months",
      features: [
        "Everything in Monthly",
        "Save $189 vs monthly billing",
        "Lock in current pricing",
        "Same full access and support"
      ],
      cta: "Get Started",
      popular: true,
      whopCheckout: 'plan_auyU5uFXjcyIE'
    },
    {
      name: "Lifetime Elite",
      price: "$2,500",
      period: "one-time",
      description: "Limited to 100 founders",
      features: [
        "Everything in Elite forever",
        "All future updates included",
        "Founders badge and priority support",
        "Never pay again"
      ],
      cta: "Join Founders",
      popular: false,
      lifetime: true,
      whopCheckout: 'plan_HgQhs2uICD7u8'
    }
  ];

  const testimonials = [
    {
      name: "Sarah Chen",
      role: "Enterprise Sales Rep",
      company: "TechCorp",
      quote: "Earn My Edge helped me close 40% more deals in 3 months. The Lumo techniques are game-changing.",
      avatar: "SC"
    },
    {
      name: "Marcus Johnson",
      role: "Sales Manager",
      company: "CloudSolutions",
      quote: "Our team's objection handling improved dramatically. The daily drills make practice actually enjoyable.",
      avatar: "MJ"
    },
    {
      name: "Emily Rodriguez",
      role: "Account Executive",
      company: "SaaS Innovate",
      quote: "The AI feedback is surprisingly accurate. It's like having a sales coach available 24/7.",
      avatar: "ER"
    }
  ];

  const stats = [
    { value: "10,000+", label: "Sales Reps Trained" },
    { value: "2.3M+", label: "Drills Completed" },
    { value: "94%", label: "Success Rate" },
    { value: "40%", label: "Avg. Deal Increase" }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(faqLd)}</script>
      </Helmet>
      {/* Ensure legacy mobile tab bar stays hidden on marketing pages */}
      <style>{`.mobile-tab-bar { display: none !important; }`}</style>
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <div className="flex items-center space-x-3">
            <img src="/assets/logo.png" alt="Earn My Edge" className="h-10 w-auto" />
            <span className="text-xl font-bold bg-gradient-to-r from-purple-600 to-blue-500 bg-clip-text text-transparent">
              Earn My Edge
            </span>
          </div>
            <div className="hidden md:flex items-center space-x-8">
              <a href="#features" className="text-muted-foreground hover:text-foreground transition-colors">
                Features
              </a>
              <a href="#pricing" className="text-muted-foreground hover:text-foreground transition-colors">
                Pricing
              </a>
              <a href="#testimonials" className="text-muted-foreground hover:text-foreground transition-colors">
                Testimonials
              </a>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/authentication?mode=login')}
              >
                Sign In
              </Button>
              <Button
                size="sm"
                onClick={() => window.location.href = whopMainUrl}
              >
                Get Started
              </Button>
            </div>
            <button
              className="md:hidden p-2 text-muted-foreground hover:text-foreground"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle menu"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          </div>
          {mobileMenuOpen && (
            <div className="md:hidden py-4 space-y-2">
              <a href="#features" className="block px-4 py-2 text-muted-foreground hover:text-foreground transition-colors">
                Features
              </a>
              <a href="#pricing" className="block px-4 py-2 text-muted-foreground hover:text-foreground transition-colors">
                Pricing
              </a>
              <a href="#testimonials" className="block px-4 py-2 text-muted-foreground hover:text-foreground transition-colors">
                Testimonials
              </a>
              <div className="px-4 pt-2 space-y-2">
                <Button
                  variant="ghost"
                  size="sm"
                  fullWidth
                  onClick={() => navigate('/authentication?mode=login')}
                >
                  Sign In
                </Button>
                <Button
                  size="sm"
                  fullWidth
                  onClick={() => window.location.href = whopMainUrl}
                >
                  Get Started
                </Button>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-primary/10 text-primary mb-6">
              <Star className="w-4 h-4 mr-2" />
              <span className="text-sm font-medium">Trusted by 10,000+ sales professionals</span>
            </div>
            <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
              Master Sales.
              <br />
              <span className="bg-gradient-to-r from-purple-600 to-blue-500 bg-clip-text text-transparent">
                Close More Deals.
              </span>
            </h1>
            <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
              Daily AI-powered drills, proprietary coaching techniques, and real-world practice to transform you into a closing machine.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                className="text-lg px-8 py-6"
                onClick={() => window.location.href = whopMainUrl}
              >
                Get Started
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="text-lg px-8 py-6"
                onClick={() => navigate('/authentication?mode=login')}
              >
                Sign In
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl font-bold text-primary mb-2">{stat.value}</div>
                <div className="text-sm text-muted-foreground">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Everything You Need to Win</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Comprehensive sales training powered by AI and proven techniques
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="p-6 rounded-xl border border-border bg-card hover:shadow-lg transition-shadow"
              >
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center text-primary mb-4">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                <p className="text-muted-foreground">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Proprietary Techniques Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-muted/30">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center px-4 py-2 rounded-full bg-accent/10 text-accent mb-6">
                <Shield className="w-4 h-4 mr-2" />
                <span className="text-sm font-medium">Exclusive Techniques</span>
              </div>
              <h2 className="text-4xl font-bold mb-6">Master the Lumo Method</h2>
              <p className="text-lg text-muted-foreground mb-6">
                Our proprietary coaching techniques are designed by top sales performers and refined through thousands of successful deals.
              </p>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <Check className="w-6 h-6 text-primary mr-3 flex-shrink-0 mt-0.5" />
                  <div>
                    <strong className="text-foreground">Mirror & Pivot:</strong>
                    <span className="text-muted-foreground"> Validate concerns while redirecting to value</span>
                  </div>
                </li>
                <li className="flex items-start">
                  <Check className="w-6 h-6 text-primary mr-3 flex-shrink-0 mt-0.5" />
                  <div>
                    <strong className="text-foreground">Shrug Close:</strong>
                    <span className="text-muted-foreground"> Casual confidence that removes pressure</span>
                  </div>
                </li>
                <li className="flex items-start">
                  <Check className="w-6 h-6 text-primary mr-3 flex-shrink-0 mt-0.5" />
                  <div>
                    <strong className="text-foreground">Frame Flip:</strong>
                    <span className="text-muted-foreground"> Recontextualize objections as opportunities</span>
                  </div>
                </li>
                <li className="flex items-start">
                  <Check className="w-6 h-6 text-primary mr-3 flex-shrink-0 mt-0.5" />
                  <div>
                    <strong className="text-foreground">+ 4 More Techniques:</strong>
                    <span className="text-muted-foreground"> Two-Minute Drill, Cascade Challenge, Human Reset, Exit Ramp</span>
                  </div>
                </li>
              </ul>
            </div>
            <div className="relative">
              <div className="aspect-square rounded-2xl bg-gradient-to-br from-primary/20 via-accent/20 to-secondary/20 p-8 flex items-center justify-center">
                <div className="text-center">
                  <Zap className="w-24 h-24 text-primary mx-auto mb-4" />
                  <p className="text-2xl font-bold">7 Proprietary</p>
                  <p className="text-xl text-muted-foreground">Coaching Techniques</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Simple, Transparent Pricing</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto mb-8">
              One Elite plan. Simple pricing. Choose monthly or annual.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {pricingPlans.map((plan, index) => (
              <div
                key={index}
                className={`relative p-8 rounded-2xl border ${
                  plan.popular
                    ? 'border-primary shadow-xl scale-105 bg-card'
                    : 'border-border bg-card'
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 bg-primary text-primary-foreground rounded-full text-sm font-medium">
                    Most Popular
                  </div>
                )}
                {plan.lifetime && (
                  <div className="absolute -top-4 right-3 px-3 py-1 bg-amber-500 text-white rounded-full text-xs font-semibold">
                    {lifetimeLeft} left
                  </div>
                )}
                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold mb-2">{plan.name}</h3>
                  <p className="text-sm text-muted-foreground mb-4">{plan.description}</p>
                  <div className="flex items-end justify-center mb-2">
                    <span className="text-5xl font-bold">{plan.price}</span>
                    <span className="text-muted-foreground ml-2">{plan.period}</span>
                  </div>
                </div>
                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-start">
                      <Check className="w-5 h-5 text-primary mr-3 flex-shrink-0 mt-0.5" />
                      <span className="text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
                <Button
                  size="lg"
                  variant={plan.popular ? 'default' : 'outline'}
                  className="w-full"
                  onClick={() => {
                    if (plan.whopCheckout) {
                      window.location.href = `https://whop.com/checkout/${plan.whopCheckout}?d2c=true`;
                    } else {
                      window.location.href = whopMainUrl;
                    }
                  }}
                >
                  {plan.cta}
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-20 px-4 sm:px-6 lg:px-8 bg-muted/30">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Trusted by Top Performers</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              See what sales professionals are saying about Earn My Edge
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="p-6 rounded-xl bg-card border border-border">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold mr-4">
                    {testimonial.avatar}
                  </div>
                  <div>
                    <div className="font-semibold">{testimonial.name}</div>
                    <div className="text-sm text-muted-foreground">
                      {testimonial.role} at {testimonial.company}
                    </div>
                  </div>
                </div>
                <p className="text-muted-foreground italic">"{testimonial.quote}"</p>
                <div className="flex mt-4 text-yellow-500">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-current" />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-muted/30">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">Frequently Asked Questions</h2>
            <p className="text-xl text-muted-foreground">Everything you need to know about Earn My Edge</p>
          </div>
          <div className="space-y-6">
            <div className="bg-card border border-border rounded-lg p-6">
              <h3 className="text-lg font-semibold mb-2">What is Earn My Edge?</h3>
              <p className="text-muted-foreground">Earn My Edge is an AI-powered sales training platform offering daily drills, objection handling practice, framework-guided lessons, and real-time coaching to help sales professionals close more deals.</p>
            </div>
            <div className="bg-card border border-border rounded-lg p-6">
              <h3 className="text-lg font-semibold mb-2">How does the AI coaching work?</h3>
              <p className="text-muted-foreground">Submit your response to a drill or scenario, and our AI coach evaluates it across clarity, confidence, control, and close. You receive targeted feedback, strengths, improvements, and a sample response to guide your practice.</p>
            </div>
            <div className="bg-card border border-border rounded-lg p-6">
              <h3 className="text-lg font-semibold mb-2">Which sales frameworks are included?</h3>
              <p className="text-muted-foreground">SPIN Selling, Sandler, Challenger, MEDDIC, NEPQ, GAP Selling, Straight Line, and Value Selling — with guided lessons and practice scenarios for each.</p>
            </div>
            <div className="bg-card border border-border rounded-lg p-6">
              <h3 className="text-lg font-semibold mb-2">Can I track my progress over time?</h3>
              <p className="text-muted-foreground">Yes. Scores are saved to your profile, XP is awarded for practice, and mastery is tracked over time. You can view your analytics and improvement trends in your dashboard.</p>
            </div>
            <div className="bg-card border border-border rounded-lg p-6">
              <h3 className="text-lg font-semibold mb-2">Is there team access and management?</h3>
              <p className="text-muted-foreground">Yes. Earn My Edge supports team accounts with role-based access (owner, manager, member) and daily team analytics to track performance and progress across your sales organization.</p>
            </div>
            <div className="bg-card border border-border rounded-lg p-6">
              <h3 className="text-lg font-semibold mb-2">What pricing options are available?</h3>
              <p className="text-muted-foreground">One Elite subscription at $97/month or $999/year (save 2 months). We also offer a limited Lifetime Elite plan at $2,500 one-time for early adopters.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to Close More Deals?
          </h2>
          <p className="text-xl text-muted-foreground mb-8">
            Join thousands of sales professionals who are mastering their craft with Earn My Edge.
          </p>
          <Button
            size="lg"
            className="text-lg px-8 py-6"
            onClick={() => window.location.href = whopMainUrl}
          >
            Get Started
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
          <p className="text-sm text-muted-foreground mt-4">
            One Elite plan. Pick monthly or annual.
          </p>
        </div>
      </section>

      {/* Footer */}
      {/* Lumo Promo teaser (static, no AI calls) */}
      <section className="px-4 sm:px-6 lg:px-8 mt-16">
        <LumoPromo />
      </section>
      <footer className="py-12 px-4 sm:px-6 lg:px-8 border-t border-border">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-5 gap-8 mb-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <img src="/assets/logo.png" alt="Earn My Edge" className="w-32 h-auto" />
              </div>
              <p className="text-sm text-muted-foreground">
                Master sales through daily practice, AI coaching, and proven techniques.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-3">Product</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><a href="#features" className="hover:text-foreground transition-colors">Features</a></li>
                <li><a href="#pricing" className="hover:text-foreground transition-colors">Pricing</a></li>
                <li><a href="#testimonials" className="hover:text-foreground transition-colors">Testimonials</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-3">Resources</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><a href="/sales-training" className="hover:text-foreground transition-colors">Sales Training</a></li>
                <li><a href="/ai-sales-coach" className="hover:text-foreground transition-colors">AI Sales Coach</a></li>
                <li><a href="/objection-handling-training" className="hover:text-foreground transition-colors">Objection Handling Training</a></li>
                <li><a href="/sales-frameworks" className="hover:text-foreground transition-colors">Sales Framework Guides</a></li>
                <li><a href="/sales-roleplay-scenarios" className="hover:text-foreground transition-colors">Roleplay Scenarios</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-3">Legal</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><a href="/privacy-policy" className="hover:text-foreground transition-colors">Privacy Policy</a></li>
                <li><a href="/terms-of-service" className="hover:text-foreground transition-colors">Terms of Service</a></li>
              </ul>
            </div>
          </div>
          <div className="pt-8 border-t border-border text-center text-sm text-muted-foreground">
            <p>&copy; 2026 Earn My Edge. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
